import { KatButton, KatIcon } from "@amzn/katal-react";
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import type { NestDocumentTypes } from "src/api/interfaces/nest";
import "./index.css";

interface NestUploadButtonProps {
  disabled: boolean;
  documentTypes: NestDocumentTypes[];
  onClick: () => void;
  onSelectDocumentType: (documentTypeId: string) => void;
}

export function NestUploadButton({
  disabled,
  documentTypes,
  onClick,
  onSelectDocumentType,
}: NestUploadButtonProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});

  useEffect(() => {
    if (!open || !toggleRef.current) return;
    const rect = toggleRef.current.getBoundingClientRect();
    setMenuStyle({
      position: "fixed",
      top: rect.bottom + 4,
      right: window.innerWidth - rect.right,
    });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        !toggleRef.current?.contains(target) &&
        !menuRef.current?.contains(target)
      )
        setOpen(false);
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [open]);

  return (
    <div className="nest-split-button">
      <KatButton
        onClick={onClick}
        className="nest-upload-button"
        disabled={disabled}
      >
        {t("aic_nest_button_upload")}
      </KatButton>
      {documentTypes.length > 0 && (
        <div className="nest-upload-dropdown">
          <button
            ref={toggleRef}
            className="nest-upload-dropdown-toggle"
            disabled={disabled}
            onClick={() => setOpen(!open)}
          >
            <KatIcon size="small" name="arrow_drop_down" />
          </button>
          {open &&
            ReactDOM.createPortal(
              <div
                ref={menuRef}
                className="nest-upload-dropdown-menu"
                style={menuStyle}
              >
                <span className="nest-upload-dropdown-header">
                  {t("aic_nest_upload_document_format")}
                </span>
                {documentTypes.map((docType) => (
                  <button
                    key={docType.id}
                    className="nest-upload-dropdown-item"
                    onClick={() => {
                      onSelectDocumentType(docType.id);
                      setOpen(false);
                    }}
                  >
                    {t(docType.name.key, docType.name.parameters)}
                  </button>
                ))}
              </div>,
              document.body
            )}
        </div>
      )}
    </div>
  );
}
